import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { AlertTriangle, Building2, Check, Home, Network, X } from "lucide-react";
import { availabilityService } from "@/services/availabilityService";
import { usersService } from "@/services/usersService";
import { propertiesService } from "@/services/propertiesService";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { cn } from "@/lib/cn";
import { AvailabilitySettingsPanel } from "@/modules/availability/AvailabilitySettingsPanel";
import { SlotPreviewPanel } from "@/modules/availability/preview/SlotPreviewPanel";
import {
  projectSlots,
  startOfWeek,
  type SlotProjection,
} from "@/modules/availability/preview/slotProjection";
import { AvailabilityDot } from "@/components/atoms/AvailabilityDot";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { InfoHint } from "@/components/atoms/InfoHint";
import { MenuSelect, type MenuSelectOption } from "@/components/molecules/MenuSelect";
import Dialog from "@/components/atoms/Dialog";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { flattenEntities, findEntityById } from "@/modules/settings/entityHelpers";
import { SaveStatus } from "@/modules/settings/SettingsPrimitives";
import type { LegalEntityHierarchy } from "@/services/profileService";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  NEW_SLOT_SETTINGS,
  isEmptySlotSettings,
  resolveSlotSettings,
  type SlotSettings,
} from "@/types/availability";
import type { PropertySummary, UserFilter, UserSummary } from "@/types/entities";

const PROPERTY_PAGE_SIZE = 30;
const PROPERTY_SEARCH_DEBOUNCE_MS = 300;
const USER_SEARCH_DEBOUNCE_MS = 300;
/** Below this the query is too broad to be worth a round trip. */
const USER_SEARCH_MIN_CHARS = 2;
const USER_SEARCH_PAGE_SIZE = 20;
/**
 * Above this many picked properties the form stops reading each one's saved row to
 * see whether they already agree — that many parallel lookups costs more than the
 * answer is worth, and the form starts blank instead.
 */
const MAX_COMPARE_PROPERTIES = 25;
/** Picked properties are saved a few at a time rather than all at once. */
const SAVE_CONCURRENCY = 4;
/** Chips beyond this are summarised as "+N more" so the scope bar keeps its height. */
const MAX_VISIBLE_PROPERTY_CHIPS = 8;

/** Plain-language warnings shown before save and in the sticky action bar (preview is xl-only). */
function collectSaveWarningLines(projection: SlotProjection): string[] {
  const lines: string[] = [];
  if (projection.windowNotApplicable) {
    lines.push(
      "Selected properties use their own availability. Saving updates slot rules only; bookable times come from each property.",
    );
    return lines;
  }
  if (!projection.active) {
    lines.push(
      "Settings are inactive. No bookable viewing slots will be generated until you turn them on.",
    );
  } else if (projection.generatedSlots === 0) {
    const first = projection.issues.find(
      (i) => i.severity === "error" || i.severity === "warning",
    );
    lines.push(
      first?.message ?? "These settings will not generate any bookable viewing slots.",
    );
  }
  for (const issue of projection.issues) {
    if (issue.severity === "error" || issue.severity === "warning") {
      if (!lines.includes(issue.message)) lines.push(issue.message);
    }
  }
  return lines;
}

/** First clause reads as a title; the rest stays body copy. */
function splitSaveWarningLine(line: string): { title: string; body?: string } {
  if (/^Settings are inactive/i.test(line)) {
    const emDash = line.indexOf(" — ");
    const dotSpace = line.indexOf(". ");
    if (dotSpace !== -1 && (emDash === -1 || dotSpace < emDash)) {
      return {
        title: line.slice(0, dotSpace + 1),
        body: line.slice(dotSpace + 2),
      };
    }
    if (emDash !== -1) {
      return {
        title: line.slice(0, emDash),
        body: line.slice(emDash + 1).trim(),
      };
    }
  }
  const sentenceEnd = line.search(/[.!?]\s+/);
  if (sentenceEnd !== -1 && line.length > sentenceEnd + 4) {
    return {
      title: line.slice(0, sentenceEnd + 1),
      body: line.slice(sentenceEnd + 1).trim(),
    };
  }
  return { title: line };
}

function SaveWarningMessageList({ lines, compact }: { lines: string[]; compact?: boolean }) {
  return (
    <ul className={compact ? "space-y-2" : "space-y-3"}>
      {lines.map((line, index) => {
        const { title, body } = splitSaveWarningLine(line);
        return (
          <li
            key={`${line}-${index}`}
            className="flex gap-2.5 text-sm leading-snug text-bridge-muted"
          >
            <AlertTriangle
              size={16}
              className="mt-0.5 shrink-0 text-amber-600"
              aria-hidden
            />
            <span className="min-w-0">
              <span className="font-semibold text-bridge-fg">{title}</span>
              {body ? <> {body}</> : null}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** A property's own saved row, or `null` when the payload is an inherited/absent one. */
function ownPropertyRow(
  payload: SlotSettings,
  propertyId: string,
): { settings: SlotSettings; activeFlag: boolean } | null {
  const raw = payload as SlotSettings & { propertyRefId?: string | null };
  if (raw.propertyRefId == null || raw.propertyRefId !== propertyId) return null;
  if (isEmptySlotSettings(payload)) return null;
  return {
    settings: { ...resolveSlotSettings(payload), users: payload.users ?? [] },
    activeFlag: raw.activeFlag === true,
  };
}

/**
 * Identity of a settings row, for deciding whether picked properties already agree.
 * Keys are sorted so two equal rows compare equal whatever order the API sent them in,
 * and the scope attribution is dropped — it says where a row lives, not what it says.
 */
function settingsSignature(row: { settings: SlotSettings; activeFlag: boolean }): string {
  const fields = { ...row.settings } as Record<string, unknown>;
  delete fields.resolvedScope;
  delete fields.resolvedFromId;
  fields.activeFlag = row.activeFlag;
  return JSON.stringify(Object.keys(fields).sort().map((key) => [key, fields[key]]));
}

/** Runs `fn` over `items`, at most `limit` in flight, keeping the input order. */
async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (let i = cursor++; i < items.length; i = cursor++) {
      try {
        results[i] = { status: "fulfilled", value: await fn(items[i]) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  });
  await Promise.all(workers);
  return results;
}

/**
 * The jump rail's targets. The ids match the section ids AvailabilitySettingsPanel
 * renders in its grouped layout.
 */
const FORM_GROUPS = [
  { id: "g-timing", label: "Timing" },
  { id: "g-window", label: "Window" },
  { id: "g-team", label: "Team" },
] as const;

/** Save targets — legal-entity-level viewing settings are not offered in the UI. */
const SAVE_SCOPE_LEVELS: {
  value: SaveScopeLevel;
  label: string;
  icon: typeof Building2;
}[] = [
  { value: "branch", label: "Branch", icon: Network },
  { value: "property", label: "Property", icon: Home },
];

/**
 * One scope's select with its label. The targeted tier is marked on the label
 * rather than with another switch — the segmented control above already owns
 * that decision, and two controls for one choice is what made the old bar
 * confusing.
 */
function ScopeField({
  label,
  icon: Icon,
  active,
  hint,
  children,
}: {
  label: string;
  icon: typeof Building2;
  active: boolean;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn(!active && "opacity-60")}>
      <div className="mb-1 flex items-center gap-1.5">
        <Icon size={12} className={cn("shrink-0", active ? "text-bridge-accent" : "text-bridge-subtle")} />
        <span
          className={cn(
            "font-mono text-[11px] font-semibold uppercase tracking-[.12em]",
            active ? "text-bridge-accent" : "text-bridge-subtle",
          )}
        >
          {label}
        </span>
        {active && (
          <span className="rounded-full bg-bridge-accent/10 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-bridge-accent">
            Saving here
          </span>
        )}
        <InfoHint content={hint} />
      </div>
      {children}
    </div>
  );
}

type SaveScopeLevel = "property" | "branch";

/**
 * Fields that must carry a value before the settings can be saved. Labels match
 * the form so the disabled-save tooltip names what the user is looking at.
 *
 * Horizon and Minimum notice are optional — the booking service falls back to
 * its own values for those. `activeFlag` is absent because unchecked means
 * false, not missing. The default availability window fields are absent too:
 * leaving that window unconfigured is a warning shown in the panel, not a
 * blocker, since the server has its own fallback window.
 *
 * Keep this in sync with the `optional` flags / required markers rendered by
 * AvailabilitySettingsPanel.
 */
const REQUIRED_FIELDS: { key: keyof SlotSettings; label: string }[] = [
  { key: "slotDuration", label: "Slot duration" },
  { key: "slotInterval", label: "Slot interval" },
  { key: "topupTravelTime", label: "Top-up time" },
  { key: "leadsPerSlot", label: "Leads per slot" },
  { key: "timezone", label: "Timezone" },
];

interface Props {
  legalEntities: LegalEntityHierarchy[];
  /** Scope the form opens on — seeded from the app context, then owned locally. */
  initialLegalEntityId?: string | null;
  initialBranchId?: string | null;
}

/**
 * Viewing settings (booking slot rules) form. The legal entity / branch /
 * property selects belong to this form only — changing them re-scopes what is
 * loaded and saved here and deliberately does NOT touch the global app context
 * shown in the header.
 */
export function ViewingSettingsSection({
  legalEntities,
  initialLegalEntityId,
  initialBranchId,
}: Props) {
  const allLegalEntities = useMemo(() => flattenEntities(legalEntities), [legalEntities]);

  // Form-local scope. The header selection is only a default for these two
  // dropdowns on the first render; from then on the dropdowns own the scope and
  // are the only thing the requests below are built from.
  const [legalEntityId, setLegalEntityId] = useState<string | null>(initialLegalEntityId ?? null);
  const [branchId, setBranchId] = useState<string | null>(initialBranchId ?? null);

  // Keep the seeded scope honest against the profile hierarchy: fall back to the
  // first legal entity when the seeded one isn't in it, and drop a seeded branch
  // that doesn't belong to the selected legal entity. Without the branch check
  // the select showed "All Branches" (the header's branch isn't one of its
  // options) while the requests still carried that branch id.
  useEffect(() => {
    if (allLegalEntities.length === 0) return;
    const selected = legalEntityId
      ? allLegalEntities.find((le) => le.id === legalEntityId)
      : undefined;
    if (!selected) {
      setLegalEntityId(allLegalEntities[0].id);
      setBranchId(null);
      setTargetScope("branch");
      return;
    }
    if (branchId && !(selected.branches ?? []).some((br) => br.id === branchId)) {
      setBranchId(null);
    }
  }, [allLegalEntities, legalEntityId, branchId]);

  // Slot settings (booking availability rules). Starts blank and stays blank
  // when the scope has nothing saved; at least one default user is mandatory.
  const [slotSettings, setSlotSettings] = useState<SlotSettings>(NEW_SLOT_SETTINGS);
  const [savingSlots, setSavingSlots] = useState(false);
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  /**
   * Users found by searching the server, kept apart from the scoped page above
   * so switching scope can drop them without disturbing the base list.
   */
  const [searchedNegotiators, setSearchedNegotiators] = useState<UserSummary[]>([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [searchingUsers, setSearchingUsers] = useState(false);
  const userSearchGeneration = useRef(0);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Optional property scope for the slot settings (empty = nothing picked). Several
  // properties can be picked at once — the same rules are then written to each of them.
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  /**
   * The picked rows, kept aside from the fetched page so a property chosen three
   * searches ago still has a label to show and can be reviewed/unpicked.
   */
  const [selectedPropertiesById, setSelectedPropertiesById] = useState<
    Record<string, PropertySummary>
  >({});
  /** True when the picked properties don't all carry the same saved settings. */
  const [mixedPropertySettings, setMixedPropertySettings] = useState(false);
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [propertiesPage, setPropertiesPage] = useState(0);
  const [propertiesHasNext, setPropertiesHasNext] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [propertySearchQuery, setPropertySearchQuery] = useState("");
  const debouncedPropertySearchQuery = useDebouncedValue(
    propertySearchQuery,
    PROPERTY_SEARCH_DEBOUNCE_MS,
  );
  const debouncedUserSearchQuery = useDebouncedValue(userSearchQuery, USER_SEARCH_DEBOUNCE_MS);
  const propertiesFetchGeneration = useRef(0);
  const fetchPropertiesRef = useRef<
    (trimmedSearch: string, page?: number, append?: boolean) => void
  >(() => undefined);
  const skipDebouncedPropertyFetchRef = useRef(false);
  const debouncedPropertySearchQueryRef = useRef(debouncedPropertySearchQuery);
  const propertiesPageRef = useRef(propertiesPage);
  const propertiesHasNextRef = useRef(propertiesHasNext);
  const loadingPropertiesRef = useRef(loadingProperties);
  /** Latest fetched page, read when a pick has to be resolved back to its row. */
  const propertiesRef = useRef<PropertySummary[]>(properties);

  useEffect(() => {
    debouncedPropertySearchQueryRef.current = debouncedPropertySearchQuery;
    propertiesRef.current = properties;
    propertiesPageRef.current = propertiesPage;
    propertiesHasNextRef.current = propertiesHasNext;
    loadingPropertiesRef.current = loadingProperties;
  }, [
    debouncedPropertySearchQuery,
    properties,
    propertiesPage,
    propertiesHasNext,
    loadingProperties,
  ]);

  // Resetting the picked properties' manually edited availability back to generated slots.
  const [confirmResetAvailability, setConfirmResetAvailability] = useState(false);
  const [resettingAvailability, setResettingAvailability] = useState(false);
  // Writing one form to many properties is confirmed before it runs.
  const [confirmBulkSave, setConfirmBulkSave] = useState(false);
  const [confirmSaveWarning, setConfirmSaveWarning] = useState(false);

  // The scope these settings are written to, chosen with the scope switches.
  // It drives the selects rather than being derived from them: a scope's own
  // dropdown only unlocks once its switch is on, and narrower scopes below the
  // target stay disabled on "All".
  const [targetScope, setTargetScope] = useState<SaveScopeLevel>("branch");
  const scopeLabel = targetScope === "property" ? "property" : "branch";

  // The saved row's `activeFlag` for the targeted scope, edited via the checkbox
  // above the form. Seeded from the backend, or false when nothing is saved —
  // the scope switches never write to it.
  const [activeFlag, setActiveFlag] = useState(false);
  const [savedActiveFlag, setSavedActiveFlag] = useState(false);

  // Baseline of the last fetched/saved settings — used to detect unsaved changes.
  const [savedSlotSettings, setSavedSlotSettings] = useState<SlotSettings>({ ...NEW_SLOT_SETTINGS });
  // Whether the scope actually has a row persisted against it. Derived from the
  // response rather than from the form values: the form starts pre-filled, so
  // "has values" and "has a saved row" are no longer the same question.
  const [savedRowExists, setSavedRowExists] = useState(false);
  const isDirty = useMemo(
    () =>
      settingsSignature({ settings: slotSettings, activeFlag }) !==
      settingsSignature({ settings: savedSlotSettings, activeFlag: savedActiveFlag }),
    [slotSettings, activeFlag, savedSlotSettings, savedActiveFlag],
  );
  const activeFlagDirty = activeFlag !== savedActiveFlag;

  /**
   * True when every selected property already carries its own availability.
   *
   * The backend marks that on the property row as a BLUE availability colour —
   * the same blue dot shown against the property in the picker and the "saving
   * to" chips. Reading it from the row the picker already loaded keeps the rule
   * the operator sees and the rule the form enforces identical, and costs no
   * extra request.
   *
   * The default availability window below only ever auto-provisions an entry
   * for a property that has none, so for these it is dead configuration and
   * demanding it before Save asks for a value nothing will read.
   */
  const windowOptional = useMemo(() => {
    if (targetScope !== "property" || propertyIds.length === 0) return false;
    const rows = propertyIds.map((id) => selectedPropertiesById[id]);
    // Every one of them, and every one known: a property whose row has not
    // loaded, or which is not blue, is a property that still needs the window.
    return rows.every((row) => row?.availabilityColor?.toUpperCase() === "BLUE");
  }, [targetScope, propertyIds, selectedPropertiesById]);

  /**
   * Per-group completeness for the rail. The rail was three links in a tall
   * empty column; carrying each group's state makes the space work and tells
   * the operator where the gap is before they hunt for it.
   */
  const groupStatus = useMemo(() => {
    const blank = (v: unknown) => v == null || v === "";
    const timingMissing = [
      slotSettings.slotDuration,
      slotSettings.slotInterval,
      slotSettings.topupTravelTime,
      slotSettings.leadsPerSlot,
    ].filter(blank).length;

    // The window half of this group only counts when the window is actually
    // used; the timezone applies either way.
    const windowMissing =
      (blank(slotSettings.timezone) ? 1 : 0) +
      (windowOptional
        ? 0
        : (slotSettings.defaultRecurrenceType == null ? 1 : 0) +
          (slotSettings.defaultRecurrenceType === "WEEKLY" &&
          (slotSettings.defaultRecurrenceDays?.length ?? 0) === 0
            ? 1
            : 0) +
          (slotSettings.defaultRecurrenceType != null &&
          (!slotSettings.defaultWindowStart || !slotSettings.defaultWindowEnd)
            ? 1
            : 0));

    const teamMissing = (slotSettings.users?.length ?? 0) === 0 ? 1 : 0;

    return {
      "g-timing": timingMissing,
      "g-window": windowMissing,
      "g-team": teamMissing,
    } as Record<string, number>;
  }, [slotSettings, windowOptional]);

  // How many settings actually differ from the saved baseline — shown next to
  // Save so committing is not a leap of faith.
  const dirtyFieldCount = useMemo(() => {
    const saved = savedSlotSettings as unknown as Record<string, unknown>;
    const current = slotSettings as unknown as Record<string, unknown>;
    const keys = new Set([...Object.keys(saved), ...Object.keys(current)]);
    let count = 0;
    for (const key of keys) {
      if (JSON.stringify(saved[key]) !== JSON.stringify(current[key])) count += 1;
    }
    return count + (activeFlagDirty ? 1 : 0);
  }, [slotSettings, savedSlotSettings, activeFlagDirty]);

  // Flipping the "Settings active" checkbox on its own writes back exactly what
  // is already saved, with a different flag — so it saves without having to
  // satisfy the field validation the untouched values may already fail.
  //
  // Never for several properties at once: there the form does not hold "what is
  // already saved" for any of them (it is blank whenever they disagree), so a
  // flag-only save would write that blank row over every one of their settings.
  const bulkPropertyScope = targetScope === "property" && propertyIds.length > 1;


  const activeFlagOnly =
    activeFlagDirty &&
    settingsSignature({ settings: slotSettings, activeFlag: false }) ===
      settingsSignature({ settings: savedSlotSettings, activeFlag: false }) &&
    !bulkPropertyScope;

  // Leaving the page, or re-scoping the form, discards unsaved edits — both go
  // through the same confirmation.
  const unsavedGuard = useUnsavedChangesGuard(isDirty);

  // Jump rail. The form pane is its own scroll container, so the scroll-spy
  // measures against that rather than the document.
  const formPaneRef = useRef<HTMLDivElement>(null);
  const [activeGroup, setActiveGroup] = useState<string>(FORM_GROUPS[0].id as string);

  // Everything that pins below the scope bar has to know how tall it is, and
  // that varies: the bar wraps to more rows as the window narrows, and gains a
  // row per scope level. Measured rather than assumed — the constant it used to
  // carry was shorter than the bar, so the rail slid underneath it.
  const scopeBarRef = useRef<HTMLDivElement>(null);
  const [scopeBarHeight, setScopeBarHeight] = useState(92);

  useEffect(() => {
    const el = scopeBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setScopeBarHeight(el.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToGroup = useCallback(
    (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      // The sticky scope bar sits over the top of the page, so land the heading
      // below it rather than underneath it.
      el.style.scrollMarginTop = `${scopeBarHeight + 12}px`;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [scopeBarHeight],
  );

  // Scroll-spy. An IntersectionObserver rather than a scroll listener on a
  // named container: the page owns the scrolling now, and the observer does not
  // care which ancestor that turns out to be.
  useEffect(() => {
    const pane = formPaneRef.current;
    if (!pane || typeof IntersectionObserver === "undefined") return;
    const targets = FORM_GROUPS.map((g) => pane.querySelector<HTMLElement>(`#${g.id}`)).filter(
      (el): el is HTMLElement => el != null,
    );
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveGroup(visible.target.id);
      },
      // Bias the band toward the top of the viewport so the group you are
      // reading is the one the rail marks, not the one merely on screen.
      { rootMargin: "-96px 0px -55% 0px", threshold: 0 },
    );
    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Load the negotiators available as "Default users" for the current scope.
  // Both selects feed the request — branch was in the dependency list but never
  // reached the params, so switching branch refetched the same unscoped list.
  // `usersService.list` resolves the legal entity to its root internally, which
  // is what `/users` expects.
  useEffect(() => {
    setLoadingUsers(true);
    usersService
      .list(
        { legalEntityId: legalEntityId ?? undefined, branchId: branchId ?? undefined },
        { page: 0, size: 100 },
      )
      .then((res) => setNegotiators((res.content ?? []).filter((u) => u.sourceCrm != null)))
      .catch(() => setNegotiators([]))
      .finally(() => setLoadingUsers(false));
    // Anyone pulled in by a server search belonged to the previous scope.
    setSearchedNegotiators([]);
  }, [legalEntityId, branchId]);

  /**
   * Server-side lookup behind the "Default users" search box.
   *
   * The list above is only the first page of `/users` for the selected scope,
   * and users hang off the root legal entity — so a negotiator who is valid for
   * a child entity, a branch or a property regularly isn't in it, and typing
   * their name returned "No matches" even though they exist. Searching the
   * server for the typed text fills that gap: scoped first, then widening to
   * the legal entity and finally to the whole organisation, so someone the
   * scope filter drops can still be found and picked.
   */
  useEffect(() => {
    const query = debouncedUserSearchQuery.trim();
    if (query.length < USER_SEARCH_MIN_CHARS) {
      setSearchingUsers(false);
      return;
    }

    const gen = ++userSearchGeneration.current;
    setSearchingUsers(true);

    const search = (params: Pick<UserFilter, "legalEntityId" | "branchId">) =>
      usersService
        .list({ ...params, search: query }, { page: 0, size: USER_SEARCH_PAGE_SIZE })
        .then((res) => (res.content ?? []).filter((u) => u.sourceCrm != null));

    void (async () => {
      try {
        let found = await search({
          legalEntityId: legalEntityId ?? undefined,
          branchId: branchId ?? undefined,
        });
        if (found.length === 0 && branchId) {
          found = await search({ legalEntityId: legalEntityId ?? undefined });
        }
        if (found.length === 0 && legalEntityId) {
          found = await search({});
        }
        if (gen !== userSearchGeneration.current) return;
        setSearchedNegotiators(found);
      } catch {
        if (gen === userSearchGeneration.current) setSearchedNegotiators([]);
      } finally {
        if (gen === userSearchGeneration.current) setSearchingUsers(false);
      }
    })();
  }, [debouncedUserSearchQuery, legalEntityId, branchId]);

  // The scoped page plus whatever the search turned up, deduped by id.
  const selectableNegotiators = useMemo(() => {
    if (searchedNegotiators.length === 0) return negotiators;
    const byId = new Set(negotiators.map((u) => u.id));
    return [...negotiators, ...searchedNegotiators.filter((u) => !byId.has(u.id))];
  }, [negotiators, searchedNegotiators]);

  // Clear the selected default users immediately whenever the branch changes —
  // the previous selection may not be valid for the newly selected branch.
  useEffect(() => {
    setSlotSettings((prev) => ({ ...prev, users: [] }));
  }, [branchId]);

  const fetchProperties = useCallback(
    (trimmedSearch: string, page = 0, append = false) => {
      if (!legalEntityId) {
        setProperties([]);
        setPropertiesPage(0);
        setPropertiesHasNext(false);
        setLoadingProperties(false);
        return;
      }

      const gen = ++propertiesFetchGeneration.current;
      const q = trimmedSearch.trim();
      setLoadingProperties(true);
      loadingPropertiesRef.current = true;

      propertiesService
        .list(
          {
            legalEntityId,
            branchId: branchId ?? undefined,
            ...(q ? { search: q } : {}),
          },
          { page, size: PROPERTY_PAGE_SIZE },
        )
        .then((res) => {
          if (gen !== propertiesFetchGeneration.current) return;
          const content = res.content ?? [];
          const nextPage = res.page ?? page;
          const hasNext =
            typeof res.hasNext === "boolean" ? res.hasNext : content.length >= PROPERTY_PAGE_SIZE;
          setPropertiesPage(nextPage);
          setPropertiesHasNext(hasNext);
          propertiesPageRef.current = nextPage;
          propertiesHasNextRef.current = hasNext;
          // Picked properties are not merged in here — they are held in
          // `selectedPropertiesById` and pinned to the top of the options list, so a
          // search never drops them out of view.
          setProperties((prev) => (append ? [...prev, ...content] : [...content]));
        })
        .catch(() => {
          if (gen !== propertiesFetchGeneration.current) return;
          if (!append) setProperties([]);
          setPropertiesHasNext(false);
          propertiesHasNextRef.current = false;
          toast.error("Failed to load properties.");
        })
        .finally(() => {
          if (gen !== propertiesFetchGeneration.current) return;
          setLoadingProperties(false);
          loadingPropertiesRef.current = false;
        });
    },
    [legalEntityId, branchId],
  );
  fetchPropertiesRef.current = fetchProperties;

  useEffect(() => {
    // A different legal entity / branch lists different properties, so anything
    // picked under the old scope no longer applies.
    setPropertyIds([]);
    setSelectedPropertiesById({});
    setPropertySearchQuery("");
    skipDebouncedPropertyFetchRef.current = true;

    if (!legalEntityId) {
      setProperties([]);
      setPropertiesPage(0);
      setPropertiesHasNext(false);
      setLoadingProperties(false);
      return;
    }

    fetchPropertiesRef.current("", 0, false);
  }, [legalEntityId, branchId]);

  useEffect(() => {
    if (!legalEntityId) return;

    if (skipDebouncedPropertyFetchRef.current) {
      if (debouncedPropertySearchQuery === "") {
        skipDebouncedPropertyFetchRef.current = false;
        return;
      }
      skipDebouncedPropertyFetchRef.current = false;
    }

    fetchPropertiesRef.current(debouncedPropertySearchQuery, 0, false);
  }, [debouncedPropertySearchQuery, legalEntityId]);

  /**
   * Picking is cumulative, so unlike the single-select version this does not clear the
   * search box — the operator is usually working through one search's results, ticking
   * several before moving on. Labels are captured here because the picked rows have to
   * outlive the page of results they came from.
   */
  const handlePropertyIdsChange = useCallback(
    (nextPropertyIds: string[]) => {
      setPropertyIds(nextPropertyIds);
      setSelectedPropertiesById((current) => {
        const updated: Record<string, PropertySummary> = {};
        for (const id of nextPropertyIds) {
          const row = current[id] ?? propertiesRef.current.find((p) => p.id === id);
          if (row) updated[id] = row;
        }
        return updated;
      });
    },
    [],
  );

  const handlePropertyEndReached = useCallback(() => {
    if (loadingPropertiesRef.current || !propertiesHasNextRef.current) return;
    fetchPropertiesRef.current(
      debouncedPropertySearchQueryRef.current,
      propertiesPageRef.current + 1,
      true,
    );
  }, []);

  // Fetch the already-saved slot settings for the current scope. Runs on mount
  // and whenever the scope (legal entity / branch / properties) changes. Falls
  // back to the system defaults when none are saved yet.
  useEffect(() => {
    // A stale response must not land on a scope the user has since moved off.
    let cancelled = false;

    const applyBaseline = (resolved: SlotSettings, serverActive: boolean, savedRow: boolean) => {
      if (cancelled) return;
      setSlotSettings(resolved);
      setSavedSlotSettings(resolved);
      setActiveFlag(serverActive);
      setSavedActiveFlag(serverActive);
      setSavedRowExists(savedRow);
    };
    const applyBlank = () => applyBaseline({ ...NEW_SLOT_SETTINGS }, false, false);

    setLoadingSlots(true);
    setMixedPropertySettings(false);

    // Several properties picked: read each one's own row and only show values that
    // every one of them already agrees on. Showing one property's settings as if
    // they were the group's would be a lie the Save button then makes true.
    if (targetScope === "property" && propertyIds.length > 1) {
      if (propertyIds.length > MAX_COMPARE_PROPERTIES) {
        // Too many to compare without hammering the API — start from a blank form,
        // which is what gets written to all of them anyway.
        applyBlank();
        setMixedPropertySettings(true);
        setLoadingSlots(false);
        return () => {
          cancelled = true;
        };
      }
      Promise.all(
        propertyIds.map((id) =>
          availabilityService
            .getSettings({ legalEntityId: legalEntityId ?? undefined, propertyId: id })
            .then((s) => ownPropertyRow(s, id))
            .catch(() => null),
        ),
      )
        .then((rows) => {
          if (cancelled) return;
          const first = rows[0];
          const allAgree =
            first != null &&
            rows.every((row) => row != null && settingsSignature(row) === settingsSignature(first));
          if (allAgree) {
            applyBaseline(first.settings, first.activeFlag, true);
          } else {
            applyBlank();
            // Only worth saying when some of them do have settings — all-blank is
            // simply "nothing configured yet", which the empty form already shows.
            setMixedPropertySettings(rows.some((row) => row != null));
          }
        })
        .catch(() => applyBlank())
        .finally(() => {
          if (!cancelled) setLoadingSlots(false);
        });
      return () => {
        cancelled = true;
      };
    }

    const propertyId = targetScope === "property" ? (propertyIds[0] ?? "") : "";
    availabilityService
      .getSettings({
        // Only what the dropdowns on this page actually have selected — a scope
        // whose select reads "All" must not narrow the lookup.
        legalEntityId: legalEntityId ?? undefined,
        branchId: branchId ?? undefined,
        propertyId: targetScope === "property" ? propertyId || undefined : undefined,
      })
      .then((s) => {
        // The endpoint cascades, so it can answer with a broader scope's row.
        // The payload names the scope it belongs to — anything that isn't this
        // scope's own row is inherited, not saved here.
        const raw = s as SlotSettings & {
          legalEntityId?: string | null;
          branchId?: string | null;
          propertyRefId?: string | null;
        };
        const belongsToTarget =
          targetScope === "property"
            ? raw.propertyRefId != null && raw.propertyRefId === propertyId
            : raw.propertyRefId == null && raw.branchId != null && raw.branchId === branchId;

        // Nothing saved for this scope — blank fields, so the form never shows
        // values that aren't persisted against it (neither system defaults nor
        // an inherited row). Otherwise take the payload as-is.
        const hasSavedRow = belongsToTarget && !isEmptySlotSettings(s);
        const resolved = hasSavedRow
          ? { ...resolveSlotSettings(s), users: s.users ?? [] }
          : { ...NEW_SLOT_SETTINGS };
        applyBaseline(resolved, hasSavedRow && raw.activeFlag === true, hasSavedRow);
      })
      .catch(() => {
        // A 404 here means "no settings for this scope", so blank fields are the
        // honest starting point for a failed lookup too.
        applyBlank();
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [legalEntityId, branchId, propertyIds, targetScope]);

  /**
   * The three scope switches behave as a radio group: they pick which scope the
   * settings are written to, and exactly one is always on. Picking one unlocks
   * its dropdown and resets every narrower scope to "All"; the current target's
   * switch can't be turned off, since some scope must own the settings.
   */
  function selectScope(scope: SaveScopeLevel) {
    if (scope === targetScope) return;
    unsavedGuard.confirm(() => applyScope(scope));
  }

  function applyScope(scope: SaveScopeLevel) {
    setTargetScope(scope);
    // Everything narrower than the new target falls back to "All" — those
    // selects are about to be disabled, so they must not keep a stale value.
    if (scope === "branch") {
      handlePropertyIdsChange([]);
    }
  }

  /**
   * Discard local edits and restore the baseline — the payload last returned by
   * `/booking/slot-settings` for this scope (or the last successful save).
   */
  function resetSlotSettings() {
    setSlotSettings(savedSlotSettings);
    setActiveFlag(savedActiveFlag);
  }

  async function resetAvailability() {
    setConfirmResetAvailability(false);
    try {
      setResettingAvailability(true);
      const results = await mapWithConcurrency(propertyIds, SAVE_CONCURRENCY, (id) =>
        propertiesService.resetAvailability(id),
      );
      const failures = results.filter((r) => r.status === "rejected");
      if (failures.length === 0) {
        toast.success(
          propertyIds.length === 1
            ? "Custom availability reset to default."
            : `Custom availability reset for ${propertyIds.length} properties.`,
        );
        return;
      }
      if (failures.length < results.length) {
        toast.warning(
          `Custom availability reset for ${results.length - failures.length} of ${results.length} properties.`,
        );
        return;
      }
      showActionErrorFromUnknown(
        (failures[0] as PromiseRejectedResult).reason,
        "Failed to reset availability.",
      );
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to reset availability.");
    } finally {
      setResettingAvailability(false);
    }
  }

  async function saveSlotSettings() {
    // Writing the same rules to several properties overwrites whatever each of them
    // has today, so it is confirmed first — unlike the single-scope save.
    if (bulkPropertyScope) {
      setConfirmBulkSave(true);
      return;
    }
    if (saveWarningLines.length > 0) {
      setConfirmSaveWarning(true);
      return;
    }
    await writeSlotSettings(targetScope === "property" ? propertyIds : [null]);
  }

  /**
   * Upserts the form's values once per target. `null` means "no property scope" —
   * the branch / legal-entity save, which writes exactly one row.
   */
  async function writeSlotSettings(targets: (string | null)[]) {
    try {
      setSavingSlots(true);
      const results = await mapWithConcurrency(targets, SAVE_CONCURRENCY, (propertyRefId) =>
        availabilityService.saveSettings({
          ...slotSettings,
          activeFlag,
          id: null,
          marketType: null,
          propertyRefId,
          branchId: branchId ?? null,
          // Must be sent explicitly. Without it a legal-entity-scoped save arrived with every scope
          // key null, and the API treated that as the GLOBAL cross-tenant default row — returning 200
          // while the agency's own settings were never created.
          legalEntityId: legalEntityId ?? null,
        }),
      );
      const failures = results.filter((r) => r.status === "rejected");
      const saved = results.length - failures.length;

      if (failures.length === 0) {
        // The form now matches what is stored everywhere it was written.
        setSavedSlotSettings(slotSettings);
        setSavedActiveFlag(activeFlag);
        setMixedPropertySettings(false);
        toast.success(
          results.length === 1
            ? "Viewing settings saved."
            : `Viewing settings saved for ${results.length} properties.`,
        );
        return;
      }
      if (saved > 0) {
        // Part of the batch still differs from the form, so the baseline is left
        // alone: the changes stay "unsaved" and Save can be pressed again.
        toast.warning(
          `Viewing settings saved for ${saved} of ${results.length} properties. Press Save to retry the rest.`,
        );
        return;
      }
      showActionErrorFromUnknown(
        (failures[0] as PromiseRejectedResult).reason,
        "Failed to save viewing settings.",
      );
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to save viewing settings.");
    } finally {
      setSavingSlots(false);
    }
  }

  const selectedLe = findEntityById(legalEntities, legalEntityId ?? "");
  const branches = selectedLe?.branches ?? [];

  const legalEntityOptions: MenuSelectOption[] = allLegalEntities.map((le) => ({
    value: le.id,
    label: le.name,
    subtitle: le.groupId,
  }));

  const branchOptions: MenuSelectOption[] = [
    { value: "", label: "All Branches" },
    ...branches.map((br) => ({
      value: br.id,
      label: br.name,
      subtitle: br.groupId,
    })),
  ];

  const toPropertyOption = (p: PropertySummary): MenuSelectOption => ({
    value: p.id,
    label: p.displayAddress ?? p.id,
    leading: <AvailabilityDot color={p.availabilityColor} />,
  });

  /**
   * Already-picked properties come first and are always present, even when the
   * current search or page doesn't contain them — the whole selection has to stay
   * reviewable while more properties are being added to it.
   */
  const propertyOptions: MenuSelectOption[] = (() => {
    const pickedRows = propertyIds
      .map((id) => selectedPropertiesById[id])
      .filter((p): p is PropertySummary => Boolean(p));
    const pickedIds = new Set(pickedRows.map((p) => p.id));
    return [
      ...pickedRows.map(toPropertyOption),
      ...properties.filter((p) => !pickedIds.has(p.id)).map(toPropertyOption),
    ];
  })();

  const selectedPropertyRows = propertyIds
    .map((id) => selectedPropertiesById[id])
    .filter((p): p is PropertySummary => Boolean(p));

  // Every settings field has to carry a value before the row can be written —
  // a partial row would leave the booking service without rules it needs. `0`
  // is a legitimate value, so only null/empty count as missing.
  const missingFields = REQUIRED_FIELDS.filter(({ key }) => {
    const v = slotSettings[key];
    return v == null || v === "";
  }).map(({ label }) => label);

  // The window itself is optional, but a half-filled one isn't: once a
  // recurrence is picked the window needs both bounds to mean anything. Missing
  // days for a WEEKLY repeat stays a warning — the panel says so inline.
  //
  // Skipped entirely when the selected properties already carry their own
  // availability: the window would never be read, so blocking Save on it asks
  // for a value that changes nothing.
  if (slotSettings.defaultRecurrenceType != null && !windowOptional) {
    if (!slotSettings.defaultWindowStart) missingFields.push("Window start");
    if (!slotSettings.defaultWindowEnd) missingFields.push("Window end");
  }

  // The targeted scope sits on "All", so there is no row to write — the form is
  // read-only until a concrete branch/property is picked. Scopes *below* the
  // target are meant to read "All"; only the target itself has to be concrete.
  const scopeIncomplete =
    (targetScope === "branch" && !branchId) ||
    (targetScope === "property" && propertyIds.length === 0);

  const saveReasons: string[] = [];
  if (targetScope === "branch" && !branchId) saveReasons.push("Select a branch");
  if (targetScope === "property" && propertyIds.length === 0)
    saveReasons.push("Select at least one property");
  if (!activeFlagOnly) {
    if (missingFields.length) saveReasons.push(`Fill in ${missingFields.join(", ")}`);
    if (!slotSettings.users?.length) saveReasons.push("Add at least one default user");
  }
  if (!isDirty) saveReasons.push("No changes to save");
  const saveDisabled = saveReasons.length > 0;

  const saveProjectionNow = new Date();
  const saveProjection = useMemo(
    () =>
      projectSlots({
        settings: slotSettings,
        weekStart: startOfWeek(saveProjectionNow),
        now: saveProjectionNow,
        scopeIncomplete,
        activeFlag,
        ownAvailability: windowOptional,
      }),
    [
      slotSettings,
      saveProjectionNow,
      scopeIncomplete,
      activeFlag,
      windowOptional,
    ],
  );
  const saveWarningLines = useMemo(
    () => (scopeIncomplete ? [] : collectSaveWarningLines(saveProjection)),
    [saveProjection, scopeIncomplete],
  );
  const showSaveWarningBanner = isDirty && !saveDisabled && saveWarningLines.length > 0;

  return (
    // Workspace layout: the expanded section claims a fixed height so the scope
    // bar, the preview and the save button can all stay on screen while the
    // operator works. Only the form pane scrolls — the original complaint was
    // that the things you need at the moment you press Save had scrolled away.
    // Plain document flow. The page is the only scroll container; the pieces
    // that must stay reachable while you work — scope, preview, actions — are
    // pinned with `sticky` instead. A fixed-height pane inside a scrolling page
    // clipped its own content the moment the viewport got shorter than it
    // assumed, which is exactly what a sticky child cannot do.
    <div className="relative">
      {(loadingSlots || loadingUsers) && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-bridge-accent border-t-transparent" />
        </div>
      )}

      {/* Docked scope bar.

          The three scope switches used to be independent-looking toggles that
          actually behaved as a radio group — exactly one could be on. A toggle
          promises "this turns on and off on its own", so it was the wrong
          control: it took three interactions to understand one decision. It is
          now a single segmented control, which says "pick one" in its shape,
          and the select below each scope enables only when it is the target. */}
      {/* Pinned to the top of the page scroll: "what am I editing" has to stay
          on screen for the whole edit. */}
      <div
        ref={scopeBarRef}
        className="sticky top-0 z-20 border-b border-bridge-border bg-white px-5 py-3"
      >
        <div data-preview-anchor="scope" className="space-y-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[.14em] text-bridge-subtle">
              Save settings at
            </span>
            <div
              role="radiogroup"
              aria-label="Scope these settings are saved at"
              className="inline-flex rounded-lg border border-bridge-border bg-bridge-bg p-0.5"
            >
              {SAVE_SCOPE_LEVELS.map((level) => {
                const selected = targetScope === level.value;
                return (
                  <button
                    key={level.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={!legalEntityId}
                    onClick={() => selectScope(level.value)}
                    className={cn(
                      "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold transition-all",
                      selected
                        ? "bg-white text-bridge-accent shadow-card"
                        : "text-bridge-muted hover:text-bridge-fg",
                      !legalEntityId && "cursor-not-allowed opacity-40",
                    )}
                  >
                    <level.icon size={13} className="shrink-0" />
                    {level.label}
                  </button>
                );
              })}
            </div>
            <InfoHint content="Which tier these settings are written to. Property overrides branch; pick a legal entity below to list its branches and properties." />

            {/* Saved `activeFlag` for the targeted scope. It rides in the
                docked bar rather than the scrolling form: "inactive" changes
                what every number in the preview means. */}
            {/* Hover explains the disabled state: a disabled input swallows its
                own pointer events, so the tooltip hangs off the label. */}
            <div data-preview-anchor="activeFlag" className="ml-auto flex items-center gap-3">
              {propertyIds.length > 0 && (
                <Tooltip
                  content={
                    propertyIds.length === 1
                      ? "Clears manually edited availability slots for this property so they fall back to these settings"
                      : `Clears manually edited availability slots for all ${propertyIds.length} selected properties so they fall back to these settings`
                  }
                  side="top"
                >
                  <Button
                    variant="outline"
                    size="sm"
                    loading={resettingAvailability}
                    onClick={() => setConfirmResetAvailability(true)}
                  >
                    Reset Custom Availability
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                content={
                  scopeIncomplete
                    ? `Select a ${scopeLabel} to activate these settings`
                    : `Off means the saved rules are ignored for this ${scopeLabel} and the next scope up applies instead.`
                }
                side="top"
              >
                <label
                  className={cn(
                    "flex items-center gap-2 rounded-lg border px-3 py-1.5 transition-colors",
                    scopeIncomplete
                      ? "cursor-not-allowed border-bridge-border text-bridge-subtle"
                      : activeFlag
                        ? "cursor-pointer border-bridge-success/40 bg-bridge-success/5 text-bridge-fg"
                        : "cursor-pointer border-bridge-warning/50 bg-bridge-warning/5 text-bridge-fg",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={activeFlag}
                    disabled={scopeIncomplete}
                    onChange={(e) => setActiveFlag(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-bridge-accent disabled:cursor-not-allowed"
                  />
                  <span className="text-[13px] font-semibold">
                    {activeFlag ? "Active" : "Inactive"}
                  </span>
                </label>
              </Tooltip>
            </div>
          </div>

          {/* One select per tier. The target's select is the only live one —
              narrower scopes are deliberately left on "All". */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <ScopeField
              label="Legal entity"
              icon={Building2}
              active={false}
              hint="Filters which branches and properties you can pick. Viewing settings are saved at branch or property level only."
            >
              <MenuSelect
                hideLabel
                value={legalEntityId ?? ""}
                options={legalEntityOptions}
                placeholder="Select legal entity"
                searchable={legalEntityOptions.length > 6}
                searchPlaceholder="Search legal entities…"
                onChange={(val) =>
                  unsavedGuard.confirm(() => {
                    setLegalEntityId(val);
                    setBranchId(null);
                  })
                }
              />
            </ScopeField>

            <ScopeField
              label="Branch"
              icon={Network}
              active={targetScope === "branch"}
              hint={
                !legalEntityId
                  ? "Select a legal entity first to list its branches."
                  : targetScope === "branch"
                    ? "The branch these settings are saved against."
                    : "Switch the scope above to Branch to save against a branch."
              }
            >
              <MenuSelect
                hideLabel
                value={branchId ?? ""}
                options={branchOptions}
                placeholder="All Branches"
                disabled={!legalEntityId || targetScope !== "branch"}
                searchable={branches.length > 6}
                searchPlaceholder="Search branches…"
                onChange={(val) => unsavedGuard.confirm(() => setBranchId(val || null))}
              />
            </ScopeField>

            <ScopeField
              label="Property"
              icon={Home}
              active={targetScope === "property"}
              hint={
                !legalEntityId
                  ? "Select a legal entity first to list its properties."
                  : targetScope !== "property"
                    ? "Switch the scope above to save against properties."
                    : "The properties these settings are saved against — pick as many as you like and they all get the same rules. Overrides their branch and legal entity."
              }
            >
              <MenuSelect
                hideLabel
                multiple
                values={propertyIds}
                options={propertyOptions}
                multiSummary={(count) => `${count} properties selected`}
                placeholder={
                  loadingProperties && properties.length === 0
                    ? "Loading properties…"
                    : "All Properties"
                }
                disabled={!legalEntityId || targetScope !== "property"}
                loading={loadingProperties}
                searchable
                searchPlaceholder="Search properties…"
                onDropdownSearchQueryChange={setPropertySearchQuery}
                onDropdownEndReached={handlePropertyEndReached}
                hasMorePages={propertiesHasNext}
                onValuesChange={(vals) =>
                  unsavedGuard.confirm(() => handlePropertyIdsChange(vals))
                }
              />
            </ScopeField>
          </div>

          {/* The picked properties, restated outside the dropdown. The list is
              built up across searches and pages, so the only place it can be
              checked in full — before one Save writes to every one of them — is
              here, next to the button that does it. */}
          {targetScope === "property" && selectedPropertyRows.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[.12em] text-bridge-subtle">
                Saving to
              </span>
              {selectedPropertyRows.slice(0, MAX_VISIBLE_PROPERTY_CHIPS).map((p) => (
                <span
                  key={p.id}
                  className="inline-flex max-w-[240px] items-center gap-1.5 rounded-full border border-bridge-accent/30 bg-bridge-accent/5 py-0.5 pl-2 pr-1 text-xs text-bridge-fg"
                >
                  <AvailabilityDot color={p.availabilityColor} />
                  <span className="truncate">{p.displayAddress ?? p.id}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${p.displayAddress ?? p.id}`}
                    onClick={() =>
                      unsavedGuard.confirm(() =>
                        handlePropertyIdsChange(propertyIds.filter((id) => id !== p.id)),
                      )
                    }
                    className="rounded-full p-0.5 text-bridge-muted hover:text-rose-500"
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
              {selectedPropertyRows.length > MAX_VISIBLE_PROPERTY_CHIPS && (
                <span className="text-xs text-bridge-muted">
                  +{selectedPropertyRows.length - MAX_VISIBLE_PROPERTY_CHIPS} more
                </span>
              )}
              <button
                type="button"
                onClick={() => unsavedGuard.confirm(() => handlePropertyIdsChange([]))}
                className="ml-1 text-[11px] font-semibold text-bridge-muted hover:text-bridge-fg"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Only shown once the picked properties are known to disagree — the
              form below is blank in that case, and Save will flatten them. */}
          {targetScope === "property" && mixedPropertySettings && (
            <p className="text-xs text-amber-700">
              The selected properties don’t currently share the same viewing settings. Saving
              applies the values below to all {propertyIds.length} of them.
            </p>
          )}
        </div>
      </div>

      {/* Form pane scrolls; the preview pane beside it holds its place. Below
          `xl` the preview drops away — there is no room for two panes, and a
          squeezed plate is worse than none. */}
      {/* The form is the surface being operated and takes the greater share;
          the preview is feedback on it and rides alongside, pinned. */}
      <div className="grid grid-cols-1 items-start xl:grid-cols-[176px_minmax(500px,1.5fr)_minmax(460px,1fr)]">
        {/* Command rail — the group nav lives in its own column rather than a
            horizontal strip, so the form keeps its full width and the current
            group is legible without reading a row of tabs. */}
        <nav
          aria-label="Setting groups"
          style={{ top: scopeBarHeight }}
          className="sticky hidden flex-col gap-0.5 self-start border-r border-bridge-border bg-white p-3 xl:flex"
        >
          {FORM_GROUPS.map((group) => {
            const missing = groupStatus[group.id] ?? 0;
            const current = activeGroup === group.id;
            return (
              <button
                key={group.id}
                type="button"
                onClick={() => scrollToGroup(group.id)}
                className={cn(
                  "group relative flex items-center gap-2.5 rounded-lg py-2.5 pl-4 pr-3 text-left text-[13.5px] font-semibold transition-colors",
                  current
                    ? "bg-bridge-accent/10 text-bridge-accent"
                    : "text-bridge-muted hover:bg-bridge-bg hover:text-bridge-fg",
                )}
              >
                {/* The current group gets a rail marker, not just a tint —
                    a colour change alone is easy to miss at this size. */}
                <span
                  className={cn(
                    "absolute inset-y-1.5 left-0 w-[3px] rounded-full transition-colors",
                    current ? "bg-bridge-accent" : "bg-transparent",
                  )}
                />
                <span className="flex-1">{group.label}</span>
                {missing > 0 ? (
                  <Tooltip
                    content={`${missing} setting${missing === 1 ? "" : "s"} still to fill in`}
                    side="right"
                  >
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-bridge-warning/20 px-1 text-[10px] font-bold tabular-nums text-amber-700">
                      {missing}
                    </span>
                  </Tooltip>
                ) : (
                  <Check size={13} className="shrink-0 text-bridge-success" />
                )}
              </button>
            );
          })}
          <p className="mt-auto px-3 text-[12.5px] leading-relaxed text-bridge-subtle">
            {scopeIncomplete
              ? `Select a ${scopeLabel} above to edit`
              : `Applies to the selected ${scopeLabel}`}
          </p>
        </nav>

        <div ref={formPaneRef}>
          <AvailabilitySettingsPanel
            value={slotSettings}
            onChange={setSlotSettings}
            users={selectableNegotiators}
            usersLoading={loadingUsers || searchingUsers}
            onUserSearchChange={setUserSearchQuery}
            disabled={scopeIncomplete}
            interactiveWindow
            grouped
            windowOptional={windowOptional}
          />
        </div>

        {/* One surface with internal bands, pinned beside the form. Its height
            is bounded so the plate always has room and the whole column stays
            within one screen; `min-h` keeps it usable when the viewport is
            short, and the column scrolls internally rather than clipping. */}
        <div
          style={{ top: scopeBarHeight }}
          className="sticky hidden max-h-[calc(100vh-11rem)] min-h-[520px] flex-col self-start overflow-hidden border-l border-bridge-border bg-white xl:flex"
        >
          <SlotPreviewPanel
            settings={slotSettings}
            savedSettings={savedSlotSettings}
            hasSavedBaseline={savedRowExists}
            scopeIncomplete={scopeIncomplete}
            activeFlag={activeFlag}
            windowOptional={windowOptional}
          />
        </div>
      </div>

      {/* Action bar — docked, and carrying the outcome, so the number and the
          button that commits it are never in different places. */}
      <div className="sticky bottom-0 z-20 rounded-b-xl bg-white shadow-[0_-1px_0_0_var(--tw-shadow-color)] shadow-bridge-border">
        {showSaveWarningBanner && (
          <div
            className="flex gap-2.5 border-t border-amber-200 bg-amber-50 px-5 py-2.5 xl:hidden"
            role="status"
          >
            <div className="min-w-0 flex-1 text-amber-950 [&_span.font-semibold]:text-amber-950">
              <SaveWarningMessageList lines={saveWarningLines} compact />
            </div>
          </div>
        )}
        <div
          className={cn(
            "flex items-center gap-3 border-t px-5 py-3 transition-colors",
            isDirty ? "border-t-bridge-warning/70" : "border-bridge-border",
          )}
        >
        <SaveStatus dirty={isDirty}>
          {dirtyFieldCount} {dirtyFieldCount === 1 ? "field" : "fields"} changed since last save
        </SaveStatus>
        <span className="ml-auto" />
        <Button
          variant="outline"
          onClick={resetSlotSettings}
          disabled={!isDirty || savingSlots}
          title="Discard changes and restore the last saved values"
        >
          Reset
        </Button>
        <Tooltip
          // A lone reason reads as a sentence; bullets only earn their keep
          // once there is a list to scan.
          content={
            saveReasons.length > 1
              ? saveReasons.map((r) => `• ${r}`).join("\n")
              : saveReasons.join("")
          }
          side="top"
        >
          <span className={saveDisabled ? "inline-flex cursor-not-allowed" : "inline-flex"}>
            <Button
              variant="primary"
              onClick={saveSlotSettings}
              loading={savingSlots}
              disabled={saveDisabled}
              className={saveDisabled ? "pointer-events-none" : undefined}
            >
              {bulkPropertyScope ? `Save for ${propertyIds.length} properties` : "Save viewing settings"}
            </Button>
          </span>
        </Tooltip>
        </div>
      </div>

      <Dialog
        open={confirmResetAvailability}
        title="Reset custom availability?"
        subtitle={
          propertyIds.length === 1
            ? "Manually edited slots for this property will be discarded."
            : `Manually edited slots for all ${propertyIds.length} selected properties will be discarded.`
        }
        submitLabel="Yes, reset"
        cancelLabel="No, keep them"
        onClose={() => setConfirmResetAvailability(false)}
        onSubmit={resetAvailability}
      >
        <p className="text-sm text-bridge-muted">
          {propertyIds.length === 1 ? "The property goes" : "The properties go"} back to the
          availability generated from these viewing settings. This cannot be undone.
        </p>
      </Dialog>

      {/* One form, many rows written. Confirmed because it silently replaces whatever
          each of those properties has configured today. */}
      <Dialog
        open={confirmBulkSave}
        title={`Apply these settings to ${propertyIds.length} properties?`}
        subtitle="Each selected property gets its own copy of the values below."
        submitLabel={`Yes, save for ${propertyIds.length}`}
        cancelLabel="No, go back"
        onClose={() => setConfirmBulkSave(false)}
        onSubmit={async () => {
          setConfirmBulkSave(false);
          await writeSlotSettings(propertyIds);
        }}
      >
        <p className="text-sm text-bridge-muted">
          Any viewing settings those properties already have are overwritten. Properties not in
          the selection are untouched.
        </p>
        {saveWarningLines.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-950 [&_span.font-semibold]:text-amber-950">
            <SaveWarningMessageList lines={saveWarningLines} compact />
          </div>
        )}
      </Dialog>

      <Dialog
        open={confirmSaveWarning}
        title="Save with these warnings?"
        subtitle={
          targetScope === "property" && propertyIds.length === 1
            ? "These viewing settings will be saved for the selected property."
            : "These viewing settings will be saved for the selected branch."
        }
        submitLabel="Yes, save anyway"
        cancelLabel="No, go back"
        submitClassName="border border-white/10 bg-bridge-accent shadow-soft hover:bg-bridge-accentSoft"
        onClose={() => setConfirmSaveWarning(false)}
        onSubmit={async () => {
          setConfirmSaveWarning(false);
          await writeSlotSettings(targetScope === "property" ? propertyIds : [null]);
        }}
      >
        <SaveWarningMessageList lines={saveWarningLines} />
      </Dialog>

      <Dialog
        open={unsavedGuard.open}
        title="Discard unsaved changes?"
        subtitle="Your edits to these viewing settings have not been saved yet."
        submitLabel="Yes, discard"
        cancelLabel="No, stay here"
        // Both the X and the overlay mean "don't do it", same as No.
        onClose={unsavedGuard.cancel}
        onSubmit={unsavedGuard.proceed}
      >
        <p className="text-sm text-bridge-muted">
          Leaving this scope will lose the changes you have made. Save them first if you want to
          keep them.
        </p>
      </Dialog>
    </div>
  );
}
